/**
 * The widget picker, as in AgencyAnalytics: a search box ("/" narrows it to
 * one source), groups on the left (Data, Content, Media), the items, and a
 * preview of the one under the pointer. Data lists a source's metrics (each
 * becomes a stat), the client's KPIs and its tables (one per dimension).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { WidgetSeed } from './new-widget.ts';
import type { Catalogue, CatalogueSource } from './reports-api.ts';

type Group = 'data' | 'content' | 'media';

const GROUP_LABEL: Record<Group, string> = { data: 'Data', content: 'Content', media: 'Media' };

export interface InserterItem {
  id: string;
  label: string;
  hint: string;
  seed: WidgetSeed;
  glyph: string;
}

const CONTENT: InserterItem[] = [
  {
    id: 'title',
    label: 'Title',
    hint: 'A section heading that groups the widgets below it.',
    seed: { type: 'HEADER' },
    glyph: 'header',
  },
  {
    id: 'textbox',
    label: 'Textbox',
    hint: 'Notes, wins and next steps for your client.',
    seed: { type: 'TEXT' },
    glyph: 'text',
  },
  {
    id: 'ai-summary',
    label: 'AI Summary',
    hint: 'SynAssistant writes a short read of this page’s numbers; you edit it.',
    seed: { type: 'AI_SUMMARY' },
    glyph: 'ai',
  },
  {
    id: 'toc',
    label: 'Table of Contents',
    hint: 'The report’s pages, numbered, always current.',
    seed: { type: 'TOC' },
    glyph: 'toc',
  },
  {
    id: 'stat',
    label: 'Stat',
    hint: 'One number, its change and a trend line.',
    seed: { type: 'METRIC' },
    glyph: 'metric',
  },
  {
    id: 'goal',
    label: 'Goal',
    hint: 'Progress towards a target.',
    seed: { type: 'GOAL' },
    glyph: 'goal',
  },
];

const MEDIA: InserterItem[] = [
  {
    id: 'image',
    label: 'Image',
    hint: 'A logo, screenshot or photo (PNG, JPEG or WebP).',
    seed: { type: 'IMAGE' },
    glyph: 'image',
  },
  {
    id: 'embed',
    label: 'Embed',
    hint: 'A web page, video or form in a frame (https only).',
    seed: { type: 'EMBED' },
    glyph: 'embed',
  },
];

type DataTab = 'metrics' | 'kpis' | 'tables';
const TAB_LABEL: Record<DataTab, string> = { metrics: 'Metrics', kpis: 'KPIs', tables: 'Tables' };

function dataItems(source: CatalogueSource, tab: 'metrics' | 'tables'): InserterItem[] {
  if (tab === 'metrics') {
    return source.metrics.map((metric) => ({
      id: `${source.key}.${metric.key}`,
      label: metric.label,
      hint: `${source.label}: ${metric.label}, its change on the previous period and a trend line.`,
      seed: { type: 'METRIC', data: { source: source.key, metrics: [metric.key] } },
      glyph: 'metric',
    }));
  }
  return source.dimensions.map((dimension) => ({
    id: `${source.key}.by.${dimension.key}`,
    label: dimension.label,
    hint: `${source.label} broken down by ${dimension.label.toLowerCase()}, with totals and changes.`,
    seed: {
      type: 'TABLE',
      data: {
        source: source.key,
        metrics: source.metrics.slice(0, 3).map((metric) => metric.key),
        dimension: dimension.key,
      },
    },
    glyph: 'table',
  }));
}

export function Inserter({
  catalogue,
  kpis = [],
  onPick,
  onClose,
}: {
  catalogue: Catalogue;
  /** The client's KPIs, each ready to drop in as a widget. */
  kpis?: InserterItem[];
  onPick: (seed: WidgetSeed) => void;
  onClose: () => void;
}): ReactNode {
  const [group, setGroup] = useState<Group>('data');
  const [query, setQuery] = useState('');
  const [sourceKey, setSourceKey] = useState(catalogue.sources[0]?.key ?? '');
  const [tab, setTab] = useState<DataTab>('metrics');
  const [hovered, setHovered] = useState<InserterItem | null>(null);
  const search = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    search.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const source = catalogue.sources.find((item) => item.key === sourceKey);
  const choosingSource = query.startsWith('/');
  const term = query.trim().toLowerCase();
  const items = useMemo(() => {
    if (choosingSource) return [];
    // Searching looks everywhere: every source's metrics and tables, content and media.
    if (term) {
      const all = [
        ...catalogue.sources.flatMap((item) => [
          ...dataItems(item, 'metrics'),
          ...dataItems(item, 'tables'),
        ]),
        ...kpis,
        ...CONTENT,
        ...MEDIA,
      ];
      return all.filter(
        (item) => item.label.toLowerCase().includes(term) || item.hint.toLowerCase().includes(term),
      );
    }
    if (group === 'content') return CONTENT;
    if (group === 'media') return MEDIA;
    if (tab === 'kpis') return kpis;
    return source ? dataItems(source, tab) : [];
  }, [catalogue.sources, kpis, choosingSource, term, group, source, tab]);
  const sourceMatches = catalogue.sources.filter((item) =>
    item.label.toLowerCase().includes(query.slice(1).trim().toLowerCase()),
  );

  return (
    <div
      className="rc-scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="ri" role="dialog" aria-modal="true" aria-label="Add a widget">
        <header className="ri-head">
          <input
            ref={search}
            type="search"
            className="ri-search"
            placeholder="Search, or type / to filter by source"
            aria-label="Search widgets"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button type="button" className="rb-icon" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="ri-body">
          <nav className="ri-groups" aria-label="Widget groups">
            {(['data', 'content', 'media'] as const).map((key) => (
              <button
                type="button"
                key={key}
                className={group === key && !term ? 'on' : ''}
                aria-pressed={group === key && !term}
                onClick={() => {
                  setGroup(key);
                  setQuery('');
                }}
              >
                {GROUP_LABEL[key]}
              </button>
            ))}
          </nav>
          <div className="ri-list">
            {choosingSource && (
              <ul aria-label="Sources">
                {sourceMatches.map((item) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      className="ri-item"
                      onClick={() => {
                        setSourceKey(item.key);
                        setGroup('data');
                        setQuery('');
                      }}
                    >
                      <strong>{item.label}</strong>
                      <small>
                        {item.metrics.length} metrics · {item.dimensions.length} tables
                      </small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!choosingSource && group === 'data' && !term && (
              <div className="ri-data-head">
                {tab !== 'kpis' && (
                  <select
                    aria-label="Source"
                    value={sourceKey}
                    onChange={(event) => setSourceKey(event.target.value)}
                  >
                    {catalogue.sources.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                )}
                <div className="rb-seg" role="tablist" aria-label="Metrics, KPIs or tables">
                  {(['metrics', 'kpis', 'tables'] as const).map((key) => (
                    <button
                      type="button"
                      role="tab"
                      key={key}
                      aria-selected={tab === key}
                      className={tab === key ? 'on' : ''}
                      onClick={() => setTab(key)}
                    >
                      {TAB_LABEL[key]}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!choosingSource && (
              <ul aria-label="Widgets">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="ri-item"
                      onMouseEnter={() => setHovered(item)}
                      onFocus={() => setHovered(item)}
                      onClick={() => onPick(item.seed)}
                    >
                      <span className={`rl-glyph rl-glyph-${item.glyph}`} aria-hidden="true" />
                      <strong>{item.label}</strong>
                    </button>
                  </li>
                ))}
                {items.length === 0 && (
                  <li className="ri-none">
                    {tab === 'kpis' && group === 'data' && !term
                      ? 'This client has no KPIs yet. Create one in its KPIs tab.'
                      : `Nothing matches “${query}”.`}
                  </li>
                )}
              </ul>
            )}
          </div>
          <aside className="ri-preview" aria-live="polite">
            {hovered ? (
              <>
                <span
                  className={`ri-preview-glyph rl-glyph rl-glyph-${hovered.glyph}`}
                  aria-hidden="true"
                />
                <strong>{hovered.label}</strong>
                <p>{hovered.hint}</p>
              </>
            ) : (
              <p className="ri-preview-empty">Point at a widget to see what it shows.</p>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
